/**
 * components/admin/InviteUserModal.tsx
 *
 * Modal for admin to invite a new user by email.
 * Flow: email form → choice step (send by email OR copy link) → success screen.
 * Created: 2026-03-25
 * Updated: 2026-03-25 - initial invite now shows choice step before sending, so admin can
 *   prepare forms before the user receives their invitation link.
 * Updated: 2026-03-25 - handleCopy now uses a ref for the timeout (prevents stale state on
 *   unmount), falls back to execCommand on clipboard API failure, and shows a toast on error.
 * Updated: 2026-04-14 - removed resend flow (prefillEmail/isResend) since resend actions
 *   are now handled inline via UserActionsDropdown. Modal is now exclusively for new invites.
 */

import { useState, useEffect, useRef } from 'react';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { Copy, Check, Mail, Link2, AlertTriangle, ArrowLeft } from 'lucide-react';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ModalStep = 'form' | 'choice' | 'success';
type SendMethod = 'email' | 'copy';

export default function InviteUserModal({
  isOpen,
  onClose,
  onSuccess,
}: InviteUserModalProps) {
  const { showToast } = useToast();
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [step, setStep] = useState<ModalStep>('form');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState<false | SendMethod>(false);
  const [error, setError] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [sendMethod, setSendMethod] = useState<SendMethod>('email');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setEmail('');
      setError('');
      setInviteUrl('');
      setEmailSent(false);
      setSendMethod('email');
      setCopied(false);
      setLoading(false);
    }
  }, [isOpen]);

  const callInviteFunction = async (method: SendMethod) => {
    setSendMethod(method);
    setError('');
    setLoading(method);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('invite-user', {
        body: { email: email.trim().toLowerCase(), sendEmail: method === 'email' },
      });
      if (fnError || !data) {
        setError('Une erreur est survenue. Veuillez réessayer.');
        return;
      }
      if (data.error) {
        setError(data.error);
        return;
      }
      if (!data.inviteUrl) {
        setError("Lien d'invitation non reçu. Veuillez réessayer.");
        return;
      }
      setInviteUrl(data.inviteUrl);
      setEmailSent(data.emailSent ?? false);
      setStep('success');
      onSuccess();
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Veuillez saisir une adresse email');
      return;
    }
    setError('');
    setStep('choice');
  };

  const handleCopy = async () => {
    let success = false;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      success = true;
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = inviteUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        success = document.execCommand('copy');
        document.body.removeChild(textarea);
      } catch {
        success = false;
      }
    }
    if (success) {
      setCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2500);
    } else {
      showToast('Impossible de copier le lien. Veuillez le sélectionner manuellement.', 'error');
    }
  };

  const handleClose = () => {
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    setStep('form');
    setEmail('');
    setError('');
    setInviteUrl('');
    setEmailSent(false);
    setCopied(false);
    setLoading(false);
    onClose();
  };

  const trimmedEmail = email.trim().toLowerCase();

  const errorBlock = error ? (
    <div className="flex items-start gap-2.5 bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-xl text-sm">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{error}</span>
    </div>
  ) : null;

  const linkCopyBlock = (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={inviteUrl}
        readOnly
        className="flex-1 min-w-0 px-3 py-2 bg-white border border-neutral-200 rounded-xl text-xs text-neutral-700 font-mono truncate focus:outline-none"
      />
      <Button
        type="button"
        variant={copied ? 'secondary' : 'primary'}
        onClick={handleCopy}
        className="shrink-0"
      >
        {copied ? (
          <><Check className="w-4 h-4 mr-1.5" />Copié</>
        ) : (
          <><Copy className="w-4 h-4 mr-1.5" />Copier</>
        )}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Inviter un utilisateur"
      size="sm"
    >
      {step === 'form' && (
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <p className="text-sm text-neutral-500 leading-relaxed">
            Saisissez l'adresse email de l'utilisateur à inviter. Vous choisirez ensuite comment lui transmettre son lien d'accès.
          </p>
          {errorBlock}
          <Input
            label="Adresse email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            required
          />
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="secondary" onClick={handleClose} className="flex-1">
              Annuler
            </Button>
            <Button type="submit" className="flex-1">
              Continuer
            </Button>
          </div>
        </form>
      )}

      {step === 'choice' && (
        <div className="space-y-4">
          <div className="bg-neutral-50 rounded-xl px-4 py-3 border border-neutral-100">
            <p className="text-xs text-neutral-400 mb-0.5">Utilisateur</p>
            <p className="text-sm font-medium text-neutral-800">{trimmedEmail}</p>
          </div>
          <p className="text-sm text-neutral-500 leading-relaxed">
            Comment souhaitez-vous transmettre le lien d'invitation&nbsp;?
          </p>
          {errorBlock}
          <div className="space-y-2 pt-1">
            <Button
              type="button"
              onClick={() => callInviteFunction('email')}
              loading={loading === 'email'}
              disabled={loading !== false}
              className="w-full"
            >
              <Mail className="w-4 h-4 mr-2" />
              Envoyer par email
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => callInviteFunction('copy')}
              loading={loading === 'copy'}
              disabled={loading !== false}
              className="w-full"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Copier le lien
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setError(''); setStep('form'); }}
              disabled={loading !== false}
              className="w-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </div>
        </div>
      )}

      {step === 'success' && sendMethod === 'email' && emailSent && (
        <div className="space-y-5">
          <div className="flex flex-col items-center text-center gap-3 pt-1">
            <div className="w-14 h-14 bg-success-50 rounded-2xl flex items-center justify-center">
              <Mail className="w-7 h-7 text-success-600" />
            </div>
            <div>
              <p className="font-semibold text-neutral-900">Email envoyé</p>
              <p className="text-sm text-neutral-500 mt-1 leading-relaxed">
                L'invitation a été envoyée à{' '}
                <span className="font-medium text-neutral-700">{trimmedEmail}</span>.{' '}
                Le lien expire dans 24&nbsp;heures.
              </p>
            </div>
          </div>
          <div className="bg-neutral-50 rounded-2xl p-4 space-y-2 border border-neutral-100">
            <p className="text-xs text-neutral-400 font-medium">Lien de secours</p>
            {linkCopyBlock}
          </div>
          <Button type="button" variant="secondary" onClick={handleClose} className="w-full">
            Fermer
          </Button>
        </div>
      )}

      {step === 'success' && sendMethod === 'email' && !emailSent && (
        <div className="space-y-5">
          <div className="flex flex-col items-center text-center gap-3 pt-1">
            <div className="w-14 h-14 bg-warning-50 rounded-2xl flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-warning-600" />
            </div>
            <div>
              <p className="font-semibold text-neutral-900">Email non envoyé</p>
              <p className="text-sm text-neutral-500 mt-1 leading-relaxed">
                Le lien a été généré mais l'envoi email a échoué. Partagez ce lien
                manuellement avec{' '}
                <span className="font-medium text-neutral-700">{trimmedEmail}</span>.
              </p>
            </div>
          </div>
          <div className="bg-neutral-50 rounded-2xl p-4 space-y-2 border border-neutral-100">
            {linkCopyBlock}
            <p className="text-xs text-neutral-400">Ce lien expire dans 24 heures.</p>
          </div>
          <Button type="button" variant="secondary" onClick={handleClose} className="w-full">
            Fermer
          </Button>
        </div>
      )}

      {step === 'success' && sendMethod === 'copy' && (
        <div className="space-y-5">
          <div className="flex flex-col items-center text-center gap-3 pt-1">
            <div className="w-14 h-14 bg-primary-50 rounded-2xl flex items-center justify-center">
              <Link2 className="w-7 h-7 text-primary-600" />
            </div>
            <div>
              <p className="font-semibold text-neutral-900">Lien généré</p>
              <p className="text-sm text-neutral-500 mt-1 leading-relaxed">
                Partagez ce lien avec{' '}
                <span className="font-medium text-neutral-700">{trimmedEmail}</span>.
              </p>
            </div>
          </div>
          <div className="bg-neutral-50 rounded-2xl p-4 space-y-2 border border-neutral-100">
            {linkCopyBlock}
            <p className="text-xs text-neutral-400">Ce lien expire dans 24 heures.</p>
          </div>
          <Button type="button" variant="secondary" onClick={handleClose} className="w-full">
            Fermer
          </Button>
        </div>
      )}
    </Modal>
  );
}
